import { useMemo, useEffect } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import type { TooltipContentProps } from "recharts";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Text } from "@phoenix/components";
import {
  ChartTooltip,
  ChartTooltipItem,
  defaultCartesianGridProps,
  defaultLegendProps,
  defaultXAxisProps,
  defaultYAxisProps,
  useBinInterval,
  useBinTimeTickFormatter,
  useCategoryChartColors,
} from "@phoenix/components/chart";
import { useTimeBinScale } from "@phoenix/hooks/useTimeBin";
import { useTimeFormatters } from "@phoenix/hooks/useTimeFormatters";
import { useUTCOffsetMinutes } from "@phoenix/hooks/useUTCOffsetMinutes";
import { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import { type ChartType } from "@phoenix/pages/project/metrics/ChartTypeRenderer";
import {
  costFormatter,
} from "@phoenix/utils/numberFormatUtils";

import { fullTimeFormatter } from "@phoenix/utils/timeFormatUtils";

import type { TraceTokenCostTimeSeriesQuery } from "./__generated__/TraceTokenCostTimeSeriesQuery.graphql";

interface Props extends ProjectMetricViewProps {
  chartType?: ChartType;
}

function TooltipContent({
  active,
  payload,
  label,
}: TooltipContentProps<number, string>) {
  const chartColors = useCategoryChartColors();
  const { fullTimeFormatter } = useTimeFormatters();
  if (active && payload && payload.length) {
    const promptValue = payload[0]?.value;
    const completionValue = payload[1]?.value;
    const promptString = costFormatter(Number(promptValue));
    const completionString = costFormatter(Number(completionValue));
    return (
      <ChartTooltip>
        {label && (
          <Text weight="heavy" size="S">{`${fullTimeFormatter(
            new Date(label)
          )
            } `}</Text>
        )}
        <ChartTooltipItem
          color={chartColors.category1}
          shape="circle"
          name="prompt"
          value={promptString}
        />
        <ChartTooltipItem
          color={chartColors.category2}
          shape="circle"
          name="completion"
          value={completionString}
        />
      </ChartTooltip>
    );
  }

  return null;
}

export function TraceTokenCostTimeSeries({
  projectId,
  timeRange,
  widgetId,
  widgetTitle,
  onDataExport,
  chartType,
}: Props) {
  const scale = useTimeBinScale({ timeRange });
  const utcOffsetMinutes = useUTCOffsetMinutes();

  const data = useLazyLoadQuery<TraceTokenCostTimeSeriesQuery>(
    graphql`
      query TraceTokenCostTimeSeriesQuery(
  $projectId: ID!
        $timeRange: TimeRange!
        $timeBinConfig: TimeBinConfig!
) {
  project: node(id: $projectId) {
          ... on Project {
      traceTokenCostTimeSeries(
        timeRange: $timeRange
              timeBinConfig: $timeBinConfig
      ) {
              data {
          timestamp
          promptCost
          completionCost
          totalCost
        }
      }
    }
  }
}
`,
    {
      projectId,
      timeRange: {
        start: timeRange.start?.toISOString(),
        end: timeRange.end?.toISOString(),
      },
      timeBinConfig: {
        scale,
        utcOffsetMinutes,
      },
    }
  );

  const chartData = useMemo(() => {
    return (data.project.traceTokenCostTimeSeries?.data ?? []).map(
      ({ timestamp, promptCost, completionCost, totalCost }) => ({
        timestamp,
        prompt: promptCost,
        completion: completionCost,
        total: totalCost,
      })
    );
  }, [data]);

  useEffect(() => {
    if (chartData.length > 0 && onDataExport && widgetId && widgetTitle) {
      onDataExport(widgetId, widgetTitle, chartData);
    }
  }, [chartData, onDataExport, widgetId, widgetTitle]);


  const timeTickFormatter = useBinTimeTickFormatter({ scale });
  const interval = useBinInterval({ scale });

  const colors = useCategoryChartColors();

  const renderChart = () => {
    if (chartType === "line") {
      return (
        <LineChart data={chartData} margin={{ top: 10, right: 16, left: 8, bottom: 20 }} syncId={"projectMetrics"}>
          <CartesianGrid {...defaultCartesianGridProps} vertical={false} />
          <XAxis
            {...defaultXAxisProps}
            dataKey="timestamp"
            interval={interval}
            tickFormatter={(x) => timeTickFormatter(new Date(x))}
          />
          <YAxis
            {...defaultYAxisProps}
            width={72}
            tickFormatter={(x: number) => costFormatter(x)}
          />
          <Tooltip
            content={TooltipContent}
            cursor={{ stroke: colors.category1, strokeWidth: 1, strokeDasharray: "4 4" }}
          />
          <Line type="monotone" dataKey="prompt" stroke={colors.category1} strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="completion" stroke={colors.category2} strokeWidth={2} dot={false} />
          <Legend {...defaultLegendProps} iconType="circle" iconSize={8} />
        </LineChart>
      );
    }

    // Default to Bar
    return (
      <BarChart
        data={chartData}
        margin={{ top: 10, right: 16, left: 8, bottom: 20 }}
        barSize={10}
        syncId={"projectMetrics"}
      >
        <CartesianGrid {...defaultCartesianGridProps} vertical={false} />
        <XAxis
          {...defaultXAxisProps}
          dataKey="timestamp"
          interval={interval}
          tickFormatter={(x) => timeTickFormatter(new Date(x))}
        />
        <YAxis
          {...defaultYAxisProps}
          width={72}
          tickFormatter={(x: number) => costFormatter(x)}
        />
        <Tooltip
          content={TooltipContent}
          cursor={{ fill: "var(--chart-tooltip-cursor-fill-color)", opacity: 0.4 }}
        />
        <Bar dataKey="prompt" stackId="a" fill={colors.category1} />
        <Bar
          dataKey="completion"
          stackId="a"
          fill={colors.category2}
          radius={[2, 2, 0, 0]}
        />
        <Legend {...defaultLegendProps} iconType="circle" iconSize={8} />
      </BarChart>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      {renderChart()}
    </ResponsiveContainer>
  );
}
